import React, { useState, useEffect, useCallback } from 'react';
import {
  IconLayoutList, IconDeviceFloppy, IconPlus, IconTrash, IconLock,
  IconChevronRight,
} from '@tabler/icons-react';
import api from '../api/api';

export default function BoardManagePage({ me, showToast, onBoardsChange }) {
  const [boards, setBoards]           = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [form, setForm]               = useState({});
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [dirty, setDirty]             = useState(false);
  const [loading, setLoading]         = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm]         = useState({ name: '', description: '', accessRole: 'USER', sortOrder: 0, listType: 'LIST', pageSize: 20 });
  const [creating, setCreating]       = useState(false);

  const isAdminOrMaster = me?.role === 'ADMIN' || me?.role === 'MASTER';

  const fetchBoards = useCallback(async () => {
    try {
      const res = await api.get('/boards/manage');
      setBoards(res.data);
    } catch (err) {
      showToast(`게시판 목록 조회 실패 (${err.response?.status ?? '네트워크 오류'})`, 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchBoards(); }, []);

  const handleSelectBoard = (board) => {
    if (dirty && !window.confirm('저장되지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
    setSelectedBoard(board);
    setForm({
      name: board.name,
      description: board.description || '',
      accessRole: board.accessRole,
      useNotice: board.useNotice,
      allowAttachment: board.allowAttachment,
      usePopup: board.usePopup,
      allowReply: board.allowReply,
      allowComment: board.allowComment,
      listType: board.listType || 'LIST',
      pageSize: board.pageSize || 20,
      sortOrder: board.sortOrder,
      isActive: board.isActive,
    });
    setDirty(false);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedBoard) return;
    setSaving(true);
    try {
      await api.put(`/boards/${selectedBoard.id}`, form);
      showToast('게시판 설정이 저장되었습니다.');
      setDirty(false);
      await fetchBoards();
      if (onBoardsChange) onBoardsChange();
    } catch {
      showToast('저장 실패', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBoard) return;
    if (!window.confirm(`"${selectedBoard.name}" 게시판을 삭제하시겠습니까?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/boards/${selectedBoard.id}`);
      showToast('게시판이 삭제되었습니다.');
      setSelectedBoard(null);
      setForm({});
      await fetchBoards();
      if (onBoardsChange) onBoardsChange();
    } catch (err) {
      showToast(err.response?.data?.error || '삭제 실패', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newForm.name.trim()) { showToast('게시판 이름을 입력하세요.', 'warning'); return; }
    setCreating(true);
    try {
      await api.post('/boards', { ...newForm, sortOrder: Number(newForm.sortOrder) || 0 });
      showToast('게시판이 생성되었습니다.');
      setShowNewModal(false);
      setNewForm({ name: '', description: '', accessRole: 'USER', sortOrder: 0, listType: 'LIST', pageSize: 20 });
      await fetchBoards();
      if (onBoardsChange) onBoardsChange();
    } catch {
      showToast('게시판 생성 실패', 'danger');
    } finally {
      setCreating(false);
    }
  };

  const accessRoleLabel = (r) => ({ USER: '전체', ADMIN: 'ADMIN 이상', MASTER: 'MASTER만' }[r] || r);

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* 상단 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="text-muted small">게시판 관리</div>
            <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>게시판 설정</h2>
          </div>
          {isAdminOrMaster && (
            <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowNewModal(true)}>
              <IconPlus size={16} /> 새 게시판 추가
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
            <div className="text-muted mt-2">로딩 중...</div>
          </div>
        ) : (
          <div className="row g-3">

            {/* 좌측: 게시판 목록 */}
            <div className="col-md-4 col-lg-3">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                    <IconLayoutList size={18} /> 게시판 목록
                  </h4>
                </div>
                <div className="list-group list-group-flush">
                  {boards.length === 0 && (
                    <div className="list-group-item text-muted text-center py-4">게시판 없음</div>
                  )}
                  {boards.map(board => {
                    const isActive = selectedBoard?.id === board.id;
                    return (
                      <div key={board.id}
                        className={`list-group-item list-group-item-action${isActive ? ' active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectBoard(board)}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="badge bg-secondary text-white" style={{ fontSize: 10 }}>
                                {accessRoleLabel(board.accessRole)}
                              </span>
                              {board.isSystem && (
                                <IconLock size={12} className={isActive ? 'text-white' : 'text-muted'} />
                              )}
                              {!board.isActive && (
                                <span className="badge bg-danger text-white" style={{ fontSize: 10 }}>비활성</span>
                              )}
                            </div>
                            <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                              {board.name}
                            </div>
                            <div className={`small ${isActive ? 'text-white opacity-75' : 'text-muted'}`}
                              style={{ fontSize: 11 }}>
                              순서: {board.sortOrder}
                            </div>
                          </div>
                          <IconChevronRight size={14} className={isActive ? 'text-white' : 'text-muted'} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 우측: 게시판 편집 */}
            <div className="col-md-8 col-lg-9">
              {!selectedBoard ? (
                <div className="card h-100 d-flex align-items-center justify-content-center"
                  style={{ minHeight: 300 }}>
                  <div className="text-center text-muted py-5">
                    <IconLayoutList size={48} className="mb-3 opacity-25" />
                    <div>좌측에서 게시판을 선택하세요</div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <div>
                      <h4 className="card-title mb-0">{selectedBoard.name}</h4>
                      <div className="text-muted small mt-1">게시판 설정을 수정하세요</div>
                    </div>
                    {isAdminOrMaster && (
                      <div className="d-flex gap-2">
                        {!selectedBoard.isSystem && (
                          <button className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                            onClick={handleDelete} disabled={deleting}>
                            {deleting
                              ? <span className="spinner-border spinner-border-sm" />
                              : <IconTrash size={16} />}
                            삭제
                          </button>
                        )}
                        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                          onClick={handleSave} disabled={saving || !dirty}>
                          {saving
                            ? <span className="spinner-border spinner-border-sm" />
                            : <IconDeviceFloppy size={16} />}
                          저장
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="card-body">
                    {!isAdminOrMaster && (
                      <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                        <IconLock size={14} className="me-1" />
                        ADMIN 이상 계정만 설정을 변경할 수 있습니다.
                      </div>
                    )}
                    {selectedBoard.isSystem && (
                      <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                        <IconLock size={14} className="me-1" />
                        시스템 기본 게시판입니다. 삭제할 수 없습니다.
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-8">
                        <label className="form-label">게시판 이름 <span className="text-danger">*</span></label>
                        <input className="form-control form-control-sm" value={form.name || ''}
                          onChange={e => handleFormChange('name', e.target.value)}
                          disabled={!isAdminOrMaster} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">정렬 순서</label>
                        <input type="number" className="form-control form-control-sm" value={form.sortOrder ?? 0}
                          onChange={e => handleFormChange('sortOrder', Number(e.target.value))}
                          disabled={!isAdminOrMaster} />
                      </div>
                      <div className="col-12">
                        <label className="form-label">설명</label>
                        <input className="form-control form-control-sm" value={form.description || ''}
                          onChange={e => handleFormChange('description', e.target.value)}
                          disabled={!isAdminOrMaster} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">접근 권한</label>
                        <select className="form-select form-select-sm" value={form.accessRole || 'USER'}
                          onChange={e => handleFormChange('accessRole', e.target.value)}
                          disabled={!isAdminOrMaster}>
                          <option value="USER">전체 (USER 이상)</option>
                          <option value="ADMIN">ADMIN 이상</option>
                          <option value="MASTER">MASTER만</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">목록 형태</label>
                        <select className="form-select form-select-sm" value={form.listType || 'LIST'}
                          onChange={e => handleFormChange('listType', e.target.value)}
                          disabled={!isAdminOrMaster}>
                          <option value="LIST">리스트형</option>
                          <option value="GALLERY">갤러리형</option>
                          <option value="BLOG">블로그형</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">페이지당 글 수</label>
                        <input type="number" className="form-control form-control-sm" value={form.pageSize ?? 20}
                          min={5} max={100}
                          onChange={e => handleFormChange('pageSize', Number(e.target.value))}
                          disabled={!isAdminOrMaster} />
                      </div>
                      <div className="col-12">
                        <div className="row g-3">
                          {[
                            { field: 'useNotice', label: '공지 사용', desc: '공지글 설정 기능 활성화' },
                            { field: 'allowAttachment', label: '파일 첨부', desc: '게시글 첨부파일 허용' },
                            { field: 'allowReply', label: '답글 허용', desc: '게시글에 답글 작성 가능' },
                            { field: 'allowComment', label: '댓글 사용', desc: '게시글에 댓글 작성 가능' },
                            { field: 'usePopup', label: '팝업 설정', desc: '대시보드 레이어 팝업 사용' },
                            { field: 'isActive', label: '게시판 활성화', desc: '비활성화 시 목록에서 숨김' },
                          ].map(({ field, label, desc }) => (
                            <div key={field} className="col-sm-6 col-lg-auto" style={{ minWidth: 180 }}>
                              <label
                                className={`d-flex align-items-center gap-3 p-3 rounded border
                                  ${form[field] ? 'border-primary bg-primary-lt' : 'border-secondary-subtle bg-light'}`}
                                style={{ cursor: isAdminOrMaster ? 'pointer' : 'default' }}
                                onClick={(e) => { e.preventDefault(); isAdminOrMaster && handleFormChange(field, !form[field]); }}>
                                <input type="checkbox" className="form-check-input mt-0 flex-shrink-0"
                                  checked={!!form[field]} onChange={() => {}} disabled={!isAdminOrMaster}
                                  style={{ pointerEvents: 'none' }} />
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: 13 }}>{label}</div>
                                  <div className="text-muted" style={{ fontSize: 11 }}>{desc}</div>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* 새 게시판 추가 모달 */}
        {showNewModal && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">새 게시판 추가</h5>
                  <button type="button" className="btn-close" onClick={() => setShowNewModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">게시판 이름 <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={newForm.name}
                      onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="게시판 이름" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">설명</label>
                    <input className="form-control form-control-sm" value={newForm.description}
                      onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="게시판 설명 (선택)" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">접근 권한</label>
                    <select className="form-select form-select-sm" value={newForm.accessRole}
                      onChange={e => setNewForm(p => ({ ...p, accessRole: e.target.value }))}>
                      <option value="USER">전체 (USER 이상)</option>
                      <option value="ADMIN">ADMIN 이상</option>
                      <option value="MASTER">MASTER만</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">목록 형태</label>
                    <select className="form-select form-select-sm" value={newForm.listType}
                      onChange={e => setNewForm(p => ({ ...p, listType: e.target.value }))}>
                      <option value="LIST">리스트형</option>
                      <option value="GALLERY">갤러리형</option>
                      <option value="BLOG">블로그형</option>
                    </select>
                  </div>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label">페이지당 글 수</label>
                      <input type="number" className="form-control form-control-sm" value={newForm.pageSize} min={5} max={100}
                        onChange={e => setNewForm(p => ({ ...p, pageSize: Number(e.target.value) }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label">정렬 순서</label>
                      <input type="number" className="form-control form-control-sm" value={newForm.sortOrder}
                        onChange={e => setNewForm(p => ({ ...p, sortOrder: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowNewModal(false)}>취소</button>
                  <button className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleCreate} disabled={creating}>
                    {creating ? <span className="spinner-border spinner-border-sm" /> : <IconPlus size={16} />}
                    추가
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
